import { useState, FormEvent } from 'react';
import { useInView } from '../../../hooks/useInView';

/* ─── Número WhatsApp no formato internacional ─── */
const WA_NUMBER = '5511984333601';

/* ─── Link direto para o WhatsApp (info lateral) ─── */
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=Ol%C3%A1%2C%20gostaria%20de%20uma%20consulta%20sobre%20direito%20imobili%C3%A1rio.`;

/**
 * Opções do select "Área de Interesse" — devem corresponder EXATAMENTE
 * aos títulos dos cards da seção "Áreas de Atuação" (PracticeAreas.tsx).
 */
const AREA_OPTIONS = [
  'Incorporação Imobiliária',
  'Regularização de Imóveis',
  'Levantamento de Área',
  'Retificação de Registro',
  'Unificação de Lotes',
  'Due Diligence',
  'Usucapião',
  'Inventários',
  'Assessoria em Compra e Venda',
];

export default function Contact() {
  const { ref: headRef, inView: headIn } = useInView();
  const { ref: leftRef, inView: leftIn } = useInView();
  const { ref: rightRef, inView: rightIn } = useInView();

  /* Estado do formulário — sem campo de telefone/WhatsApp */
  const [form, setForm] = useState({
    name: '',
    email: '',
    area: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /**
   * Ao submeter:
   * 1. Previne comportamento padrão (sem reload, sem fetch).
   * 2. Monta a mensagem no formato exato solicitado.
   * 3. Codifica com encodeURIComponent.
   * 4. Abre o WhatsApp em nova aba com a mensagem pronta.
   */
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    /* Validação básica — todos os campos são obrigatórios */
    if (!form.name || !form.email || !form.area || !form.message) return;
    if (form.message.length > 500) return;

    /* Monta a mensagem no formato exato */
    const rawMessage =
      `Nome Completo: ${form.name} | ` +
      `E-mail: ${form.email} | ` +
      `Área de Interesse: ${form.area} | ` +
      `Motivo do Contato: ${form.message}`;

    /* Codifica para URL */
    const encodedMessage = encodeURIComponent(rawMessage);

    /* Abre o WhatsApp em nova aba com a mensagem pronta */
    window.open(`https://wa.me/${WA_NUMBER}?text=${encodedMessage}`, '_blank');

    /* Limpa o formulário após o envio */
    setForm({ name: '', email: '', area: '', message: '' });
  };

  /* Estilo reutilizável para inputs/textarea */
  const inputStyle: React.CSSProperties = {
    color: '#F5F5F5',
    border: '1px solid rgba(212,175,55,0.3)',
    borderRadius: '2px',
  };

  const focusBorder = (el: HTMLElement) => {
    (el as HTMLElement).style.borderColor = '#D4AF37';
  };
  const blurBorder = (el: HTMLElement) => {
    (el as HTMLElement).style.borderColor = 'rgba(212,175,55,0.3)';
  };

  return (
    <section id="contato" className="w-full py-24 md:py-32" style={{ background: '#0A2A52' }}>
      <div className="max-w-6xl mx-auto px-6 md:px-12">

        {/* ── Header ── */}
        <div
          ref={headRef}
          className={`text-center mb-16 transition-all duration-1000 ${headIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-16" style={{ background: 'linear-gradient(90deg, transparent, #D4AF37)' }}></div>
            <span className="font-sans text-xs tracking-[3px] uppercase font-medium" style={{ color: '#D4AF37' }}>
              Fale Conosco
            </span>
            <div className="h-px w-16" style={{ background: 'linear-gradient(90deg, #D4AF37, transparent)' }}></div>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl font-bold" style={{ color: '#F5F5F5' }}>
            Entre em <span style={{ color: '#D4AF37' }}>Contato</span>
          </h2>
          <p className="font-sans text-sm md:text-base font-light mt-4 max-w-xl mx-auto" style={{ color: '#D9D9D9' }}>
            Solicite sua análise jurídica e dê o primeiro passo para proteger seu patrimônio.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">

          {/* ── Formulário ── */}
          <div
            ref={leftRef}
            className={`lg:col-span-3 transition-all duration-1000 ${leftIn ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
          >
            <form
              onSubmit={handleSubmit}
              id="contato-form"
              noValidate
              className="flex flex-col gap-5"
            >

              {/* Nome Completo */}
              <div className="flex flex-col gap-2">
                <label className="font-sans text-xs tracking-wider uppercase font-medium" style={{ color: '#D4AF37' }}>
                  Nome Completo *
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Seu nome completo"
                  required
                  className="w-full px-4 py-3 text-sm font-sans outline-none transition-all duration-200 bg-transparent"
                  style={inputStyle}
                  onFocus={(e) => focusBorder(e.currentTarget)}
                  onBlur={(e) => blurBorder(e.currentTarget)}
                />
              </div>

              {/* E-mail */}
              <div className="flex flex-col gap-2">
                <label className="font-sans text-xs tracking-wider uppercase font-medium" style={{ color: '#D4AF37' }}>
                  E-mail *
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="seu@email.com"
                  required
                  className="w-full px-4 py-3 text-sm font-sans outline-none transition-all duration-200 bg-transparent"
                  style={inputStyle}
                  onFocus={(e) => focusBorder(e.currentTarget)}
                  onBlur={(e) => blurBorder(e.currentTarget)}
                />
              </div>

              {/* Área de Interesse */}
              <div className="flex flex-col gap-2">
                <label className="font-sans text-xs tracking-wider uppercase font-medium" style={{ color: '#D4AF37' }}>
                  Área de Interesse *
                </label>
                <select
                  name="area"
                  value={form.area}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm font-sans outline-none transition-all duration-200 cursor-pointer"
                  style={{
                    background: '#081F3D',
                    color: form.area ? '#F5F5F5' : 'rgba(245,245,245,0.5)',
                    border: '1px solid rgba(212,175,55,0.3)',
                    borderRadius: '2px',
                  }}
                  onFocus={(e) => focusBorder(e.currentTarget)}
                  onBlur={(e) => blurBorder(e.currentTarget)}
                >
                  <option value="">Selecione a área de interesse</option>
                  {AREA_OPTIONS.map((opt) => (
                    <option
                      key={opt}
                      value={opt}
                      style={{ background: '#081F3D', color: '#F5F5F5' }}
                    >
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mensagem */}
              <div className="flex flex-col gap-2">
                <label className="font-sans text-xs tracking-wider uppercase font-medium" style={{ color: '#D4AF37' }}>
                  Mensagem *{' '}
                  <span className="normal-case font-normal" style={{ color: 'rgba(212,175,55,0.6)' }}>
                    ({form.message.length}/500)
                  </span>
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Descreva brevemente sua necessidade jurídica..."
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 text-sm font-sans outline-none transition-all duration-200 resize-none bg-transparent"
                  style={inputStyle}
                  onFocus={(e) => focusBorder(e.currentTarget)}
                  onBlur={(e) => blurBorder(e.currentTarget)}
                />
                {form.message.length >= 500 && (
                  <p className="font-sans text-xs" style={{ color: '#E5C76B' }}>
                    Limite de 500 caracteres atingido.
                  </p>
                )}
              </div>

              {/* Botão Enviar */}
              <button
                type="submit"
                disabled={form.message.length > 500}
                className="flex items-center justify-center gap-2 w-full py-4 font-sans font-semibold text-sm tracking-wider uppercase whitespace-nowrap cursor-pointer transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: '#D4AF37', color: '#0A2A52', borderRadius: '2px' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = '#C29B2E';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = '#D4AF37';
                }}
              >
                <i className="ri-whatsapp-line"></i>
                Enviar pelo WhatsApp
              </button>

              {/* Nota informativa */}
              <p className="font-sans text-xs text-center" style={{ color: 'rgba(217,217,217,0.55)' }}>
                Ao clicar em "Enviar", você será redirecionado ao WhatsApp com sua mensagem já preenchida.
              </p>

            </form>
          </div>

          {/* ── Informações de contato ── */}
          <div
            ref={rightRef}
            className={`lg:col-span-2 flex flex-col gap-8 transition-all duration-1000 delay-200 ${rightIn ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
          >
            {/* WhatsApp */}
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-4 p-5 cursor-pointer transition-all duration-300 group"
              style={{ background: 'rgba(18,59,115,0.4)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '2px' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(212,175,55,0.5)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(212,175,55,0.2)'; }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-sm flex-shrink-0" style={{ background: '#D4AF37' }}>
                <i className="ri-whatsapp-line text-xl" style={{ color: '#0A2A52' }}></i>
              </div>
              <div>
                <p className="font-sans text-xs tracking-wider uppercase font-medium mb-1" style={{ color: '#D4AF37' }}>WhatsApp</p>
                <p className="font-serif text-lg font-semibold" style={{ color: '#F5F5F5' }}>(11) 98433-3601</p>
                <p className="font-sans text-xs font-light mt-0.5" style={{ color: '#D9D9D9' }}>Clique para conversar</p>
              </div>
            </a>

            {/* Endereço */}
            <div
              className="flex items-start gap-4 p-5"
              style={{ background: 'rgba(18,59,115,0.4)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '2px' }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-sm flex-shrink-0" style={{ background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.3)' }}>
                <i className="ri-map-pin-line text-xl" style={{ color: '#D4AF37' }}></i>
              </div>
              <div>
                <p className="font-sans text-xs tracking-wider uppercase font-medium mb-1" style={{ color: '#D4AF37' }}>Endereço</p>
                <p className="font-sans text-sm font-medium" style={{ color: '#F5F5F5' }}>R. Califórnia, 773 – Sala 7</p>
                <p className="font-sans text-sm font-light" style={{ color: '#D9D9D9' }}>Brooklin – São Paulo/SP</p>
                <p className="font-sans text-xs font-light mt-1" style={{ color: 'rgba(217,217,217,0.7)' }}>CEP: 04566-061</p>
              </div>
            </div>

            {/* Instagram */}
            <a
              href="https://instagram.com/uitalo.adv"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-4 p-5 cursor-pointer transition-all duration-300"
              style={{ background: 'rgba(18,59,115,0.4)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '2px' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(212,175,55,0.5)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(212,175,55,0.2)'; }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-sm flex-shrink-0" style={{ background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.3)' }}>
                <i className="ri-instagram-line text-xl" style={{ color: '#D4AF37' }}></i>
              </div>
              <div>
                <p className="font-sans text-xs tracking-wider uppercase font-medium mb-1" style={{ color: '#D4AF37' }}>Instagram</p>
                <p className="font-serif text-lg font-semibold" style={{ color: '#F5F5F5' }}>@uitalo.adv</p>
                <p className="font-sans text-xs font-light mt-0.5" style={{ color: '#D9D9D9' }}>Acompanhe nosso conteúdo</p>
              </div>
            </a>
          </div>

        </div>
      </div>
    </section>
  );
}
